
import { supabase } from "@/integrations/supabase/client";

export type SupportedPlatform = 'facebook' | 'twitter' | 'instagram' | 'linkedin' | 'youtube';

interface OAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
  authUrl: string;
}

// Configuration for different platforms
const oauthConfigs: Record<SupportedPlatform, OAuthConfig> = {
  facebook: {
    clientId: process.env.FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID',
    redirectUri: `${window.location.origin}/auth/callback/facebook`,
    scope: 'pages_show_list,pages_read_engagement,pages_manage_posts',
    authUrl: 'https://www.facebook.com/v14.0/dialog/oauth'
  },
  twitter: {
    clientId: process.env.TWITTER_CLIENT_ID || 'YOUR_TWITTER_CLIENT_ID',
    redirectUri: `${window.location.origin}/auth/callback/twitter`,
    scope: 'tweet.read,tweet.write,users.read',
    authUrl: 'https://twitter.com/i/oauth2/authorize'
  },
  instagram: {
    clientId: process.env.INSTAGRAM_APP_ID || 'YOUR_INSTAGRAM_APP_ID',
    redirectUri: `${window.location.origin}/auth/callback/instagram`,
    scope: 'user_profile,user_media',
    authUrl: 'https://api.instagram.com/oauth/authorize'
  },
  linkedin: {
    clientId: process.env.LINKEDIN_CLIENT_ID || 'YOUR_LINKEDIN_CLIENT_ID',
    redirectUri: `${window.location.origin}/auth/callback/linkedin`,
    scope: 'r_liteprofile,r_emailaddress,w_member_social',
    authUrl: 'https://www.linkedin.com/oauth/v2/authorization'
  },
  youtube: {
    clientId: process.env.YOUTUBE_CLIENT_ID || 'YOUR_YOUTUBE_CLIENT_ID',
    redirectUri: `${window.location.origin}/auth/callback/youtube`,
    scope: 'https://www.googleapis.com/auth/youtube',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth'
  }
};

// Save state for verification
const saveOAuthState = (state: string, platform: SupportedPlatform) => {
  localStorage.setItem('oauthState', state);
  localStorage.setItem('oauthPlatform', platform);
  localStorage.setItem('oauthRedirect', window.location.href);
};

// Generate a state parameter for CSRF protection
const generateState = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export const initiatePlatformOAuth = (platform: SupportedPlatform) => {
  const config = oauthConfigs[platform];
  if (!config) {
    throw new Error(`Unsupported platform: ${platform}`);
  }

  const state = generateState();
  saveOAuthState(state, platform);

  // Create the authorization URL with appropriate parameters
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    response_type: 'code',
    state
  });

  // Special handling for Twitter OAuth 2.0
  if (platform === 'twitter') {
    params.append('code_challenge', state);
    params.append('code_challenge_method', 'plain');
  }

  // Special handling for Google/YouTube OAuth
  if (platform === 'youtube') {
    params.append('access_type', 'offline');
    params.append('prompt', 'consent');
  }

  // Redirect to the authorization URL
  window.location.href = `${config.authUrl}?${params.toString()}`;
};

export const processOAuthCallback = async (code: string, state: string) => {
  // Verify state to prevent CSRF attacks
  const savedState = localStorage.getItem('oauthState');
  const platform = localStorage.getItem('oauthPlatform') as SupportedPlatform | null;
  const redirectUrl = localStorage.getItem('oauthRedirect');

  if (!savedState || !platform || savedState !== state) {
    throw new Error('OAuth state mismatch. Authentication failed.');
  }

  // Exchange the code for an access token using a Supabase function
  // to avoid exposing client secrets in the frontend
  const { data, error } = await supabase.functions.invoke(`exchange-${platform}-token`, {
    body: { code, redirect_uri: oauthConfigs[platform].redirectUri }
  });

  if (error) {
    throw new Error(`Failed to exchange OAuth code: ${error.message}`);
  }

  // Clear OAuth state from localStorage
  localStorage.removeItem('oauthState');
  localStorage.removeItem('oauthPlatform');
  localStorage.removeItem('oauthRedirect');

  // Return to the original page
  if (redirectUrl) {
    window.location.href = redirectUrl;
  }

  return data;
};

export const verifyOAuthToken = async (platform: SupportedPlatform, token: string) => {
  // Use Supabase edge function to verify token
  const { data, error } = await supabase.functions.invoke(`verify-${platform}-token`, {
    body: { token }
  });

  if (error) {
    return { isValid: false, error: error.message };
  }

  return { isValid: true, data };
};
